import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { BillingFeature, NewReportSlideFragmentLayout, PopupEvent } from 'webapp/enums/enums';

import joinClasses from 'webapp/helpers/join-classes';
import safeStyle from 'webapp/helpers/safe-style';
import CommonUiIcon from 'webapp/components/common/ui-icon';

type Option = PopupListItem<''>;

interface ReportsFragmentGridAddFragmentButtonComponentInterface {
	Args: {
		isVisible?: boolean;
		extraClass?: string;
		hideTitle?: boolean;
		canAddDataboardSlide?: boolean;
		onAddFragment: (fragmentType: NewReportSlideFragmentLayout) => void;
		backgroundColor?: string | null; // needed to set background color for the button wrapper on slides
	};
}

export default class ReportsFragmentGridAddFragmentButtonComponent extends Component<ReportsFragmentGridAddFragmentButtonComponentInterface> {
	@service('billing') declare billingService: Service.Billing;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/report-slide-fragments')
	declare mmReportSlideFragmentsService: Service.ModelManager.ReportSlideFragments;

	@tracked isPopupOpen = false;
	isAddDisabled = false;

	get options(): Option[] {
		const options: Option[] = [
			{
				id: NewReportSlideFragmentLayout.METRIC,
				text: 'Metric',
				icon: 'Metrics',
			},
		];

		if (this.args.canAddDataboardSlide) {
			options.push({
				id: NewReportSlideFragmentLayout.DATABOARD,
				text: 'Dashboard',
				icon: 'Databoards',
			});
		}

		options.push(
			{
				id: NewReportSlideFragmentLayout.AI_PERFORMANCE_SUMMARY,
				text: 'AI performance summary',
				icon: 'AiFill',
			},
			{
				id: NewReportSlideFragmentLayout.DATA_STORY,
				text: 'Data story',
				icon: 'Sparkles',
			},
		);

		if (!this.args.hideTitle) {
			options.push(
				{
					id: NewReportSlideFragmentLayout.TITLE,
					text: 'Header',
					icon: 'H1',
				},
				{
					id: NewReportSlideFragmentLayout.TITLE_2,
					text: 'Header 2',
					icon: 'H2',
				},
			);
		}

		options.push(
			{
				id: NewReportSlideFragmentLayout.TEXT,
				text: 'Text',
				icon: 'TextColor',
			},
			{
				id: NewReportSlideFragmentLayout.IMAGE,
				text: 'Image',
				icon: 'Image',
			},
			{
				id: NewReportSlideFragmentLayout.TABLE,
				text: 'Table',
				icon: 'Table',
			},
			{
				id: NewReportSlideFragmentLayout.DIVIDER,
				text: 'Divider',
				icon: 'Divider',
			},
		);

		options.forEach((option) => {
			const limitation = this.getLimitationForLayout(
					option.id as NewReportSlideFragmentLayout,
				),
				isDisabled = !!limitation && !this.billingService.checkIfCan(limitation);
			if (isDisabled) {
				option.isDisabled = true;
				option.extra = {
					type: 'featureLimitation',
					feature: limitation,
					trackingOrigin:
						limitation === BillingFeature.GENERATIVE_AI
							? 'generative_ai_block'
							: undefined,
					text: 'To use this block, upgrade your plan.',
				};
			}
		});

		return options;
	}

	@action
	openPopupFragmentType(event: MouseEvent) {
		if (!this.args.isVisible) {
			return;
		}

		if (this.isPopupOpen) {
			return;
		}

		const target = event.target as HTMLElement;
		const attachTo = target.querySelector<HTMLElement>('ui-icon') || target;

		const popup = this.popupService.createListPopup(attachTo, '', 'popup-type-fragment-type');

		if (popup) {
			popup.open({
				itemsList: this.options,
				doAction: this.doOptionsAction,
			});

			this.isPopupOpen = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				this.isPopupOpen = false;
			});
		}
	}

	@action
	doOptionsAction(_actionName: unknown, rawId: unknown) {
		if (this.isAddDisabled) {
			return;
		}

		const id = rawId as NewReportSlideFragmentLayout;

		const item = this.options.find((o) => o.id === id);
		if (item && !item.isDisabled) {
			this.isAddDisabled = true;
			this.args.onAddFragment(id);

			// re-enable after a short delay to prevent double clicks
			setTimeout(() => {
				this.isAddDisabled = false;
			}, 300);
		}
	}

	private getLimitationForLayout(layout: NewReportSlideFragmentLayout) {
		return this.mmReportSlideFragmentsService.getLimitationFromNewFragmentLayout(layout);
	}

	<template>
		<div
			class="add-fragment-button-wrapper"
			style={{if @backgroundColor (safeStyle @backgroundColor property="background-color")}}
		>
			<div
				class={{joinClasses
					(array
						"add-fragment-button"
						(if this.isPopupOpen "add-fragment-button--active")
						@extraClass
					)
				}}
				role="button"
				{{on "click" this.openPopupFragmentType}}
			>
				<CommonUiIcon @name="Plus" @size="sm" />
			</div>
		</div>
	</template>
}
